import { useNavigate, useLocation } from 'react-router-dom';
import { useHomepage } from '@/contexts/homepage-context';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { cn } from '@/lib/utils';
import { pillWidthClass } from './dropdownStyles';

import { DomainDropdown } from './topToolbar/DomainDropdown';
import { ImplicitPrimitivesDropdown } from './topToolbar/ImplicitPrimitivesDropdown';
import { NasaLatticeDropdown } from './topToolbar/NasaLatticeDropdown';
import { NasaUnitcellConverterDropdown } from './topToolbar/NasaUnitcellConverterDropdown';
import { WheelsDropdown } from './topToolbar/WheelsDropdown';
import { HeatExchangerDropdown } from './topToolbar/HeatExchangerDropdown';
import { SupinInletDropdown } from './topToolbar/SupinInletDropdown';
import { GenericSubTab } from './topToolbar/GenericSubTab';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for when Marketplace button is un-hidden, not deleted
import { MarketplaceButton } from './topToolbar/MarketplaceButton';
import { NavButtons } from './topToolbar/NavButtons';
import { CadModeToggle } from './topToolbar/CadModeToggle';
import { ManualCadFeatureBar } from './topToolbar/ManualCadFeatureBar';
import { FocusedPill } from './topToolbar/FocusedPill';
import { useToolbarNav } from './topToolbar/useToolbarNav';
import {
  DOMAINS,
  DOMAIN_SUBTABS,
  DEFAULT_SUBTAB,
  SubTab,
  Domain,
} from './topToolbar/data';

// Flatten every domain's sub-tabs into a value→SubTab lookup so a focused pill
// (identified only by its value in the nav history) can be rendered in the
// domain slot with its full label/hero.
const SUBTAB_BY_VALUE: Record<string, SubTab> = Object.values(
  DOMAIN_SUBTABS,
).reduce<Record<string, SubTab>>((acc, tabs) => {
  for (const tab of tabs) acc[tab.value] = tab;
  return acc;
}, {});

export function TopToolbar() {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for when Marketplace button is un-hidden, not deleted
  const navigate = useNavigate();
  const location = useLocation();
  const {
    vehicleEnabled,
    textToCadSelected,
    setTextToCadSelected,
    vehicleTab,
    vehicleSubTab,
    setVehicleEnabled,
    setVehicleTab,
    setVehicleSubTab,
    cadMode,
    setCadMode,
    featureTreeOpen,
    supinInletType,
    setSupinInletType,
    implicitPrimitive,
    setImplicitPrimitive,
    setImplicitPrimitiveParams,
  } = useHomepage();
  const isLg = useMediaQuery('(min-width: 1024px)');

  // Manual CAD (FreeCAD-style) mode is flag-gated (rule 7): the toggle stays
  // hidden — and the mode forced to Automatic — until the backend ships and the
  // 'manual-cad' flag is flipped (lib/featureFlags.ts). Flag OFF → prod is
  // completely unchanged. See docs/freecad-manual-cad-integration-plan.md.
  const manualCadEnabled = useFeatureFlag('manual-cad', true);
  const isManualCad = manualCadEnabled && cadMode === 'manual';

  // Browser-style navigation history over the (domain, sub-feature) selection.
  // Purely presentational: `focus()` records a clicked pill, Back/Forward walk
  // the stack. The underlying vehicleTab/vehicleSubTab setters are unchanged.
  const nav = useToolbarNav();

  // Wrap a sub-tab click so it both applies the selection (via `apply`) AND
  // pushes a focused view onto the nav history — collapsing the bar to just the
  // clicked pill (+ Marketplace) until the user navigates Back.
  const focusOnSelect = (apply: (value: string) => void) => (value: string) => {
    apply(value);
    nav.focus(value);
  };

  const sidebarWidth = isLg
    ? featureTreeOpen
      ? '16.75rem'
      : '0.75rem'
    : '0px';

  const isFiles =
    location.pathname === '/files' || location.pathname.startsWith('/files/');
  const isMarket =
    location.pathname === '/market' || location.pathname.startsWith('/market/');

  if (isFiles || isMarket) return null;

  const currentDomain: Domain = vehicleEnabled
    ? (vehicleTab as Domain)
    : textToCadSelected
      ? 'default'
      : ('none' as Domain);
  const currentLabel = vehicleEnabled
    ? (DOMAINS.find((d) => d.value === vehicleTab)?.label ?? '')
    : textToCadSelected
      ? 'Text or Image to CAD'
      : null;

  const currentHero = vehicleEnabled
    ? DOMAINS.find((d) => d.value === vehicleTab)?.hero
    : undefined;

  // Sub-tabs come from the *active* domain, vehicle OR text-to-cad. Without
  // including the textToCadSelected branch the Implicit Primitives sub-tab
  // never appears, since 'default' isn't a vehicle.
  const subTabs = vehicleEnabled
    ? (DOMAIN_SUBTABS[vehicleTab] ?? [])
    : textToCadSelected
      ? (DOMAIN_SUBTABS['default'] ?? [])
      : [];

  const handleDomainSelect = (domain: Domain) => {
    // Picking a domain starts a fresh browsing session — clear the history so
    // Back doesn't walk into a stale, unrelated focused pill.
    nav.reset();
    if (domain === 'default') {
      setVehicleEnabled(false);
      setTextToCadSelected(true);
      setVehicleSubTab(DEFAULT_SUBTAB['default'] ?? '');
    } else {
      setVehicleEnabled(true);
      setTextToCadSelected(false);
      setVehicleTab(domain);
      setVehicleSubTab(DEFAULT_SUBTAB[domain] ?? '');
    }
  };

  // When a pill has been clicked (`focused` view), it replaces the domain slot
  // and the rest of the feature bar goes blank — only Marketplace survives.
  const focusedTab =
    nav.current.kind === 'focused'
      ? SUBTAB_BY_VALUE[nav.current.subTab]
      : undefined;
  const isFocused = !!focusedTab;

  const renderSubTab = (tab: SubTab) => {
    if (tab.disabled) {
      return (
        <GenericSubTab
          key={tab.value}
          tab={tab}
          vehicleSubTab={vehicleSubTab}
          setVehicleSubTab={setVehicleSubTab}
        />
      );
    }

    if (tab.value === 'implicit-primitives') {
      return (
        <ImplicitPrimitivesDropdown
          key={tab.value}
          tab={tab}
          vehicleSubTab={vehicleSubTab}
          implicitPrimitive={implicitPrimitive}
          setVehicleSubTab={focusOnSelect(setVehicleSubTab)}
          setImplicitPrimitive={setImplicitPrimitive}
          setImplicitPrimitiveParams={setImplicitPrimitiveParams}
        />
      );
    }

    if (tab.value === 'nasa-lattice-primitives') {
      return (
        <NasaLatticeDropdown
          key={tab.value}
          tab={tab}
          vehicleSubTab={vehicleSubTab}
          implicitPrimitive={implicitPrimitive}
          setVehicleSubTab={focusOnSelect(setVehicleSubTab)}
          setImplicitPrimitive={setImplicitPrimitive}
          setImplicitPrimitiveParams={setImplicitPrimitiveParams}
        />
      );
    }

    if (tab.value === 'nasa-unitcell-converter') {
      return <NasaUnitcellConverterDropdown key={tab.value} tab={tab} />;
    }

    if (tab.value === 'wheels') {
      return (
        <WheelsDropdown
          key={tab.value}
          tab={tab}
          vehicleSubTab={vehicleSubTab}
          implicitPrimitive={implicitPrimitive}
          setVehicleSubTab={focusOnSelect(setVehicleSubTab)}
          setImplicitPrimitive={setImplicitPrimitive}
          setImplicitPrimitiveParams={setImplicitPrimitiveParams}
        />
      );
    }

    if (tab.value === 'heat-exchanger') {
      return (
        <HeatExchangerDropdown
          key={tab.value}
          tab={tab}
          vehicleSubTab={vehicleSubTab}
          implicitPrimitive={implicitPrimitive}
          setVehicleSubTab={focusOnSelect(setVehicleSubTab)}
          setImplicitPrimitive={setImplicitPrimitive}
          setImplicitPrimitiveParams={setImplicitPrimitiveParams}
        />
      );
    }

    if (tab.value === 'nasa-supersonic-inlet') {
      return (
        <SupinInletDropdown
          key={tab.value}
          tab={tab}
          vehicleSubTab={vehicleSubTab}
          supinInletType={supinInletType}
          setVehicleSubTab={focusOnSelect(setVehicleSubTab)}
          setSupinInletType={setSupinInletType}
        />
      );
    }

    return (
      <GenericSubTab
        key={tab.value}
        tab={tab}
        vehicleSubTab={vehicleSubTab}
        // The auto-open-first-window behavior lives in
        // useAutoOpenFirstFeatureWindow (mounted once in FeatureTreeSidebar),
        // which reacts to the resolved surface changing — covers this pill AND
        // the compound dropdowns below uniformly. focusOnSelect just collapses
        // the bar to this pill.
        setVehicleSubTab={focusOnSelect(setVehicleSubTab)}
      />
    );
  };

  return (
    <>
      {/* Controls toolbar — starts at the right edge of the sidebar. Overall
          dimensions (height h-28, left/right anchoring) are unchanged; the bar
          is now a vertical stack: a thin nav-button row on top, then the pill
          row anchored to the SAME bottom edge as before. */}
      <div
        className="fixed right-0 top-[39px] z-30 hidden h-28 flex-col border-b border-telekinesis-neutral-700 transition-[left] duration-200 lg:flex"
        style={{
          left: sidebarWidth,
          // Surface the active tab merges into: bg MUST equal the active-tab
          // fill (#16191b) so the tab body and this toolbar read as one shape.
          // The seam (1px #323232 top line) is owned by AppMenuBar and drawn
          // full-width across the whole bar bottom — we deliberately do NOT add
          // a borderTop here, or it would double that seam from x >= sidebar.
          backgroundColor: '#16191b',
        }}
      >
        {/* Top-left nav row — browser-style Back/Forward + the Auto/Manual
            toggle. The toggle is h-6 (== the nav buttons) so it never grows the
            row. The inner box is exactly `pillWidthClass` wide (matching the
            domain pill's own fixed width, same px-4 left offset as the pill
            row below) with `justify-between`, so the toggle's RIGHT edge
            always lands on the domain pill's right edge, regardless of either
            component's own natural width. */}
        <div className="flex items-center px-4 pt-2.5">
          <div
            className={cn(pillWidthClass, 'flex items-center justify-between')}
          >
            <NavButtons
              canBack={nav.canBack}
              canForward={nav.canForward}
              onBack={nav.back}
              onForward={nav.forward}
            />
            {/* Manual/Automatic CAD toggle — separated from Back/Forward.
                Flag-gated: hidden until the Manual backend ships. */}
            {manualCadEnabled && (
              <CadModeToggle cadMode={cadMode} setCadMode={setCadMode} />
            )}
          </div>
        </div>

        {/* Pill row — a SHORT fixed-height (h-[54px]) strip, vertically CENTERED
            in the remaining bar space (items-center) so there's even breathing
            room above (from the toggle row) and below (from the bar edge). The
            pills self-stretch to this height, so shrinking the strip shortens
            them without touching each pill component. Every pill shares
            `pillWidthClass` (184px); when more sub-tabs are present than the
            row can fit at that width (e.g. Land's 4), they shrink together
            (default flex-shrink) rather than any one overflowing — each
            pill's text truncates to an ellipsis at that point instead of
            wrapping. */}
        <div className="flex flex-1 items-center px-4">
          <div className="flex h-[54px] w-full items-stretch gap-3">
            {/* Feature slot: the domain dropdown normally, OR the focused pill
                once a feature has been clicked. */}
            {isFocused && focusedTab ? (
              <FocusedPill tab={focusedTab} />
            ) : (
              <DomainDropdown
                vehicleTab={vehicleTab}
                currentDomain={currentDomain}
                currentLabel={currentLabel}
                currentHero={currentHero}
                handleDomainSelect={handleDomainSelect}
              />
            )}

            {/* Manual CAD → the FreeCAD-style feature bar replaces the per-domain
                AI sub-tabs (the vehicle pill above stays). Otherwise the normal
                sub-tabs, hidden while a pill is focused (bar goes blank except
                Marketplace). */}
            {isManualCad
              ? !isFocused && <ManualCadFeatureBar />
              : !isFocused && subTabs.map((tab) => renderSubTab(tab))}

            <div className="flex-1" />

            {/* Marketplace — temporarily hidden from the frontend (component kept, not deleted). */}
            {/* <MarketplaceButton navigate={navigate} /> */}
          </div>
        </div>
      </div>
    </>
  );
}
